"use client";

import { DesignSystemProvider } from "@repo/design-system";
import { TooltipProvider } from "@repo/design-system/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { bind, setEnabled } from "cuelume";
import { usePathname } from "next/navigation";
import { Suspense, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { queryClient } from "@/lib/queryClient";

interface ProvidersProps {
  readonly children: ReactNode;
}

function WorkspaceTheme({ onChange }: { onChange: (theme: "light" | undefined) => void }) {
  const pathname = usePathname();
  const isWorkspace = pathname === "/workspace" || pathname.startsWith("/workspace/");
  useLayoutEffect(() => { onChange(isWorkspace ? "light" : undefined); }, [isWorkspace, onChange]);
  return null;
}

export function AppDesignSystemProvider({ children }: ProvidersProps) {
  const [forcedTheme, setForcedTheme] = useState<"light" | undefined>();
  return <DesignSystemProvider forcedTheme={forcedTheme}>
    <Suspense fallback={null}><WorkspaceTheme onChange={setForcedTheme} /></Suspense>
    {children}
  </DesignSystemProvider>;
}

export const Providers = ({ children }: ProvidersProps) => {
  useEffect(() => {
    bind();

    const savedPreference = window.localStorage.getItem(
      "swansonking.interaction-sounds"
    );
    setEnabled(savedPreference !== "muted");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delay={0}>
        {children}
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
};
